import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { commissionRangesAPI, commissionPackagesAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Layers, Plus, Pencil, Trash2, Loader2, Save, Filter } from 'lucide-react';

const OPERATORS = [
  { value: '', label: 'All Operators' },
  ...['jio', 'airtel', 'vi', 'bsnl_topup', 'bsnl_special', 'jio_lite', 'mtnl', 'tatasky', 'dishtv', 'd2h', 'airtel_dth', 'sun_direct']
    .map(o => ({ value: o, label: o.replace('_', ' ') }))
];

const CommissionRanges = () => {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterPkg, setFilterPkg] = useState('');
  const [form, setForm] = useState({
    min_amount: 0, max_amount: 0, commission_type: 'percentage',
    retailer_commission: 0, distributor_commission: 0, super_distributor_commission: 0,
    operator: '', package_id: '', status: 'active'
  });

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchItems(); }, [filterPkg]);

  const fetchAll = async () => {
    try { const p = await commissionPackagesAPI.list(); setPackages(p.data || []); } catch {}
    fetchItems();
  };
  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterPkg) params.package_id = filterPkg;
      const r = await commissionRangesAPI.list(params);
      setItems(r.data || []);
    } catch {} finally { setLoading(false); }
  };

  const reset = () => { setForm({ min_amount: 0, max_amount: 0, commission_type: 'percentage', retailer_commission: 0, distributor_commission: 0, super_distributor_commission: 0, operator: '', package_id: '', status: 'active' }); setEditId(null); };
  const openEdit = (i) => {
    setForm({
      min_amount: i.min_amount || 0, max_amount: i.max_amount || 0,
      commission_type: i.commission_type || 'percentage', retailer_commission: i.retailer_commission || 0,
      distributor_commission: i.distributor_commission || 0, super_distributor_commission: i.super_distributor_commission || 0,
      operator: i.operator || '', package_id: i.package_id || '', status: i.status
    });
    setEditId(i.id); setDialogOpen(true);
  };

  const handleSave = async () => {
    if (parseFloat(form.min_amount) < 0 || parseFloat(form.max_amount) <= 0) { toast.error('Valid amount range is required'); return; }
    if (parseFloat(form.min_amount) >= parseFloat(form.max_amount)) { toast.error('Max amount must be greater than min amount'); return; }
    setSaving(true);
    try {
      if (editId) await commissionRangesAPI.update(editId, form); else await commissionRangesAPI.create(form);
      toast.success(editId ? 'Updated' : 'Created'); fetchItems(); setDialogOpen(false); reset();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; try { await commissionRangesAPI.delete(id); toast.success('Deleted'); fetchItems(); } catch { toast.error('Failed'); } };
  const handleToggle = async (i) => { try { await commissionRangesAPI.update(i.id, { status: i.status === 'active' ? 'inactive' : 'active' }); fetchItems(); } catch {} };

  const fmtComm = (val, type) => type === 'percentage' ? `${val}%` : `₹${val}`;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="commission-ranges-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Layers className="w-6 h-6" style={{ color: '#4680ff' }} />Commission by Range</h1>
            <p className="text-muted-foreground">Set tiered commission rates based on transaction amount</p>
          </div>
          <Button onClick={() => { reset(); setDialogOpen(true); }} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="add-range-btn"><Plus className="w-4 h-4 mr-2" />Add Range</Button>
        </div>

        {/* Filter */}
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filterPkg} onValueChange={setFilterPkg}>
              <SelectTrigger className="w-[200px]" data-testid="filter-range-pkg"><SelectValue placeholder="All Packages" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Packages</SelectItem>
                {packages.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {filterPkg && <Button variant="ghost" size="sm" onClick={() => setFilterPkg('')}>Clear</Button>}
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
            : items.length === 0 ? <div className="text-center py-12 text-muted-foreground"><Layers className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No commission ranges configured</p><p className="text-sm mt-1">Create ranges like ₹1-100, ₹101-500 with different commission rates</p></div>
              : <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Amount Range</TableHead><TableHead>Operator</TableHead><TableHead>Package</TableHead><TableHead>Type</TableHead><TableHead>Retailer</TableHead><TableHead>Distributor</TableHead><TableHead>Super Dist.</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{items.map(i => (
                  <TableRow key={i.id}>
                    <TableCell className="font-semibold">₹{parseFloat(i.min_amount).toLocaleString()} — ₹{parseFloat(i.max_amount).toLocaleString()}</TableCell>
                    <TableCell className="capitalize">{i.operator ? i.operator.replace('_', ' ') : <span className="text-muted-foreground text-xs">All</span>}</TableCell>
                    <TableCell><Badge variant="secondary">{i.package_name || 'Default'}</Badge></TableCell>
                    <TableCell className="capitalize text-xs">{i.commission_type}</TableCell>
                    <TableCell className="font-semibold text-green-700">{fmtComm(i.retailer_commission, i.commission_type)}</TableCell>
                    <TableCell className="font-semibold text-blue-700">{fmtComm(i.distributor_commission, i.commission_type)}</TableCell>
                    <TableCell className="font-semibold text-purple-700">{fmtComm(i.super_distributor_commission, i.commission_type)}</TableCell>
                    <TableCell><button onClick={() => handleToggle(i)}><Badge className={i.status === 'active' ? 'bg-green-100 text-green-800 cursor-pointer' : 'bg-red-100 text-red-800 cursor-pointer'}>{i.status}</Badge></button></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(i)}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(i.id)}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}</TableBody></Table></div>}
        </CardContent></Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogContent className="max-w-lg" data-testid="range-dialog"><DialogHeader><DialogTitle>{editId ? 'Edit' : 'Add'} Commission Range</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Min Amount (₹) *</Label><Input type="number" min="0" step="1" value={form.min_amount} onChange={e => setForm({ ...form, min_amount: e.target.value })} data-testid="range-min" /></div>
              <div className="space-y-2"><Label>Max Amount (₹) *</Label><Input type="number" min="1" step="1" value={form.max_amount} onChange={e => setForm({ ...form, max_amount: e.target.value })} data-testid="range-max" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Operator (optional)</Label>
                <Select value={form.operator} onValueChange={v => setForm({ ...form, operator: v })}><SelectTrigger data-testid="range-op"><SelectValue placeholder="All Operators" /></SelectTrigger>
                  <SelectContent>{OPERATORS.map(o => <SelectItem key={o.value || 'all'} value={o.value || 'all'} className="capitalize">{o.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-2"><Label>Commission Type</Label>
                <Select value={form.commission_type} onValueChange={v => setForm({ ...form, commission_type: v })}><SelectTrigger data-testid="range-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percentage">Percentage (%)</SelectItem><SelectItem value="flat">Flat (₹)</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label>Retailer</Label><Input type="number" step="0.01" min="0" value={form.retailer_commission} onChange={e => setForm({ ...form, retailer_commission: e.target.value })} data-testid="range-retailer" /></div>
              <div className="space-y-2"><Label>Distributor</Label><Input type="number" step="0.01" min="0" value={form.distributor_commission} onChange={e => setForm({ ...form, distributor_commission: e.target.value })} data-testid="range-distributor" /></div>
              <div className="space-y-2"><Label>Super Dist.</Label><Input type="number" step="0.01" min="0" value={form.super_distributor_commission} onChange={e => setForm({ ...form, super_distributor_commission: e.target.value })} data-testid="range-superdist" /></div>
            </div>
            <div className="space-y-2"><Label>Commission Package</Label>
              <Select value={form.package_id} onValueChange={v => setForm({ ...form, package_id: v })}><SelectTrigger data-testid="range-package"><SelectValue placeholder="Default (no package)" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Default (no package)</SelectItem>{packages.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button onClick={handleSave} disabled={saving} className="bg-[#4680ff] hover:bg-[#2563eb] text-white shadow-lg shadow-blue-500/20 rounded-lg h-10 px-5 text-sm font-semibold btn-press" data-testid="save-range-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{editId ? 'Update' : 'Add'}</Button></DialogFooter>
        </DialogContent></Dialog>
      </div>
    </DashboardLayout>
  );
};
export default CommissionRanges;
